/**
 * Ranked belt progressions (low → high). Kebab-case tokens; stored/API values use PascalCase in pickers.
 * Simplified club-agnostic orders — clubs may use local variants.
 */

import { getMaxStripesForBeltSystem, LEGACY_BELT_SYSTEM_DEFAULT, validationBeltSystemKeyFromGroup } from "./beltSystemCatalog";

export const BJJ_BELT_PROGRESSION = [
  "white",
  "grey-white",
  "grey",
  "grey-black",
  "yellow-white",
  "yellow",
  "yellow-black",
  "orange-white",
  "orange",
  "orange-black",
  "green-white",
  "green",
  "green-black",
  "blue",
  "purple",
  "brown",
  "black",
  "coral",
];

/** BJJ adult ranks (self-signup 16+, promotion order above black). */
export const BJJ_ADULT_RANK_BELTS_KEBAB = [
  "white",
  "blue",
  "purple",
  "brown",
  "black",
  "coral",
];

export const JUDO_BELT_PROGRESSION = [
  "white",
  "yellow",
  "orange",
  "green",
  "blue",
  "brown",
  "black",
];

export const KARATE_BELT_PROGRESSION = [
  "white",
  "yellow",
  "orange",
  "green",
  "blue",
  "purple",
  "brown",
  "black",
];

export const TAEKWONDO_BELT_PROGRESSION = [
  "white",
  "yellow",
  "green",
  "blue",
  "red",
  "black",
];

export const AIKIDO_BELT_PROGRESSION = ["white", "brown", "black"];

export const HAPKIDO_BELT_PROGRESSION = [
  "white",
  "yellow",
  "green",
  "blue",
  "red",
  "black",
];

/** FIAS-style simplified */
export const SAMBO_BELT_PROGRESSION = ["white", "blue", "purple", "black"];

export const KUNG_FU_BELT_PROGRESSION = [
  "white",
  "yellow",
  "orange",
  "green",
  "blue",
  "purple",
  "brown",
  "black",
];

export const KENDO_BELT_PROGRESSION = ["white", "blue", "brown", "black"];

export const JUJUTSU_BELT_PROGRESSION = [
  "white",
  "yellow",
  "orange",
  "green",
  "blue",
  "brown",
  "black",
];

/** Glove rank colors (simplified as belt ranks) */
export const SAVATE_BELT_PROGRESSION = [
  "blue",
  "green",
  "red",
  "white",
  "yellow",
  "silver",
  "gold",
];

export const KRAV_MAGA_BELT_PROGRESSION = [
  "white",
  "yellow",
  "orange",
  "green",
  "blue",
  "brown",
  "black",
];

/** Capoeira cordões (simplified ascending) */
export const CAPOEIRA_BELT_PROGRESSION = [
  "crua",
  "white",
  "yellow",
  "orange",
  "red",
  "blue",
  "green",
  "purple",
  "brown",
  "black",
];

/** @type {Record<string, string[]>} */
export const RANKED_BELT_PROGRESSIONS = {
  bjj: BJJ_BELT_PROGRESSION,
  judo: JUDO_BELT_PROGRESSION,
  karate: KARATE_BELT_PROGRESSION,
  taekwondo: TAEKWONDO_BELT_PROGRESSION,
  aikido: AIKIDO_BELT_PROGRESSION,
  hapkido: HAPKIDO_BELT_PROGRESSION,
  sambo: SAMBO_BELT_PROGRESSION,
  kung_fu: KUNG_FU_BELT_PROGRESSION,
  kendo: KENDO_BELT_PROGRESSION,
  jujutsu: JUJUTSU_BELT_PROGRESSION,
  savate: SAVATE_BELT_PROGRESSION,
  krav_maga: KRAV_MAGA_BELT_PROGRESSION,
  capoeira: CAPOEIRA_BELT_PROGRESSION,
};

/**
 * @param {string} kebab e.g. grey-white, kung_fu handled as single segment keys
 */
export function kebabToPromotionPickerValue(kebab) {
  if (!kebab) return "";
  return kebab
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
}

/** BJJ promotion picker order (low → high): kids + adult belts in rank order. */
export const BJJ_PROMOTION_PICKER_BELTS_PASCAL = BJJ_BELT_PROGRESSION.map(
  kebabToPromotionPickerValue
);

/** BJJ kids belts (excluded from self-signup; min age 16). */
const BJJ_KIDS_KEBAB = new Set(
  BJJ_BELT_PROGRESSION.filter((k) => !BJJ_ADULT_RANK_BELTS_KEBAB.includes(k))
);

/**
 * Self-signup belt picker: adult ranks only (no kids belts on BJJ).
 * @param {string|null|undefined} beltSystemKey
 * @returns {string[]}
 */
export function getSignupAdultBeltOrder(beltSystemKey) {
  const key = (beltSystemKey || "bjj").toLowerCase();
  if (key === "bjj") {
    return BJJ_ADULT_RANK_BELTS_KEBAB.map(kebabToPromotionPickerValue);
  }
  const progression = RANKED_BELT_PROGRESSIONS[key];
  if (!progression) {
    return BJJ_ADULT_RANK_BELTS_KEBAB.map(kebabToPromotionPickerValue);
  }
  return progression.map(kebabToPromotionPickerValue);
}

/**
 * @param {string|null|undefined} beltSystemKey
 * @returns {string[]|null}
 */
export function getBeltProgressionOrder(beltSystemKey) {
  const key = (beltSystemKey || "bjj").toLowerCase();
  return RANKED_BELT_PROGRESSIONS[key] || null;
}

export function isBeltRankUiConfigured(beltSystemKey) {
  return getBeltProgressionOrder(beltSystemKey) != null;
}

export function isBeltRankVectorUi(beltSystemKey) {
  const key = (beltSystemKey || "bjj").toLowerCase();
  return key !== "bjj";
}

/**
 * @param {string|null|undefined} beltSystemKey
 * @returns {string[]|null}
 */
export function getPromotionPickerBeltOrder(beltSystemKey) {
  const key = (beltSystemKey || "bjj").toLowerCase();
  if (key === "bjj") return BJJ_PROMOTION_PICKER_BELTS_PASCAL;
  const progression = RANKED_BELT_PROGRESSIONS[key];
  if (!progression) return null;
  return progression.map(kebabToPromotionPickerValue);
}

/**
 * Unique belt-rank system keys from a member's training groups (multi-sport).
 * @param {Array<string|number>} groupIDs
 * @param {object[]} allGroups
 */
export function collectBeltSystemKeysFromGroupIDs(groupIDs, allGroups) {
  const keys = [];
  for (const id of groupIDs || []) {
    const g = allGroups.find((gr) => String(gr.groupID) === String(id));
    if (!g) continue;
    const vk = validationBeltSystemKeyFromGroup(g);
    if (vk && !keys.includes(vk)) keys.push(vk);
  }
  return keys.length ? keys : [LEGACY_BELT_SYSTEM_DEFAULT];
}

/**
 * Pick which sport's progression a stored belt belongs to (for display / edit).
 * @param {string} belt
 * @param {string[]} candidateKeys
 * @param {string} [fallbackKey]
 */
export function inferBeltSystemKeyForBelt(
  belt,
  candidateKeys,
  fallbackKey = LEGACY_BELT_SYSTEM_DEFAULT
) {
  const fallback = (fallbackKey || LEGACY_BELT_SYSTEM_DEFAULT).toLowerCase();
  const pool = [...new Set((candidateKeys || []).map((k) => String(k).toLowerCase()).filter(Boolean))];
  const keys = pool.length ? pool : [fallback];
  const matches = keys.filter((k) => getBeltRankIndex(belt, k) >= 0);
  if (matches.length === 0) return fallback;
  if (matches.length === 1) return matches[0];
  if (matches.includes(fallback)) return fallback;
  return matches[0];
}

/**
 * Primary validation key from first ranked group (legacy display default).
 */
export function displayValidationKeyFromGroupIDs(groupIDs, allGroups) {
  for (const id of groupIDs || []) {
    const g = allGroups.find((gr) => String(gr.groupID) === String(id));
    if (g) return validationBeltSystemKeyFromGroup(g);
  }
  return LEGACY_BELT_SYSTEM_DEFAULT;
}

/**
 * Match stored belt (any casing) to a picker option for MUI Select value.
 * @param {string|null|undefined} stored
 * @param {string[]|null|undefined} beltOrder
 */
export function resolvePromotionBeltPickerValue(stored, beltOrder) {
  if (stored == null || stored === "") return "";
  if (!beltOrder?.length) return String(stored);
  const s = String(stored);
  if (beltOrder.includes(s)) return s;
  const norm = s.toLowerCase().replace(/[-\s]/g, "");
  const matched = beltOrder.find((b) => b.toLowerCase().replace(/[-\s]/g, "") === norm);
  return matched || "";
}

/** @param {string} belt */
export function normalizeBeltForRank(belt) {
  if (!belt || typeof belt !== "string") return "";
  return belt.toLowerCase().replace(/[-\s]/g, "").trim();
}

/**
 * @param {string} belt
 * @param {string} [beltSystemKey]
 * @returns {number}
 */
export function getBeltRankIndex(belt, beltSystemKey = "bjj") {
  const normalized = normalizeBeltForRank(belt);
  if (!normalized) return -1;
  const key = (beltSystemKey || "bjj").toLowerCase();
  const progression = RANKED_BELT_PROGRESSIONS[key] || RANKED_BELT_PROGRESSIONS.bjj;
  const idx = progression.findIndex((p) => normalizeBeltForRank(p) === normalized);
  return idx >= 0 ? idx : -1;
}

/**
 * Compare two belt+stripe ranks (mirrors backend beltValidation).
 * @returns {number} 1 if A > B, -1 if A < B, 0 if equal
 */
export function compareBeltAndStripe(beltA, stripeA, beltB, stripeB, beltSystemKey = "bjj") {
  const rankA = getBeltRankIndex(beltA, beltSystemKey);
  const rankB = getBeltRankIndex(beltB, beltSystemKey);

  if (rankA >= 0 && rankB >= 0) {
    if (rankA > rankB) return 1;
    if (rankA < rankB) return -1;
  } else if (rankA >= 0 && rankB < 0) return 1;
  else if (rankA < 0 && rankB >= 0) return -1;

  const maxStripes = getMaxStripesForBeltSystem(beltSystemKey);
  if (maxStripes <= 0) return 0;

  const sA = Number(stripeA) || 0;
  const sB = Number(stripeB) || 0;
  if (sA > sB) return 1;
  if (sA < sB) return -1;
  return 0;
}

/** Promotion must be strictly higher than the member's current rank. */
export function isPromotionRankHigher(
  candidateBelt,
  candidateStripe,
  currentBelt,
  currentStripe,
  beltSystemKey = "bjj"
) {
  return (
    compareBeltAndStripe(
      candidateBelt,
      candidateStripe,
      currentBelt,
      currentStripe,
      beltSystemKey
    ) > 0
  );
}

/**
 * Whether a belt color may be chosen in the promotion picker.
 * Stripe sports (BJJ, etc.): current color and higher colors — stripes are chosen separately.
 * Non-stripe sports: only strictly higher belt colors.
 */
export function isBeltColorPromotionAllowed(
  pickerBelt,
  currentBelt,
  currentStripe,
  beltSystemKey = "bjj"
) {
  const maxStripes = getMaxStripesForBeltSystem(beltSystemKey);
  const rankPicker = getBeltRankIndex(pickerBelt, beltSystemKey);
  const rankCurrent = getBeltRankIndex(currentBelt, beltSystemKey);

  if (maxStripes > 0) {
    if (rankPicker >= 0 && rankCurrent >= 0) {
      return rankPicker >= rankCurrent;
    }
    const normPicker = normalizeBeltForRank(pickerBelt);
    const normCurrent = normalizeBeltForRank(currentBelt);
    if (normPicker && normCurrent && normPicker === normCurrent) {
      return true;
    }
    return (
      compareBeltAndStripe(pickerBelt, 0, currentBelt, currentStripe, beltSystemKey) > 0
    );
  }

  return (
    compareBeltAndStripe(pickerBelt, 0, currentBelt, currentStripe, beltSystemKey) >
      0
  );
}

/** Stripe option for a chosen promotion belt color. */
export function isStripePromotionAllowed(
  promotionBelt,
  candidateStripe,
  currentBelt,
  currentStripe,
  beltSystemKey = "bjj"
) {
  return (
    compareBeltAndStripe(
      promotionBelt,
      candidateStripe,
      currentBelt,
      currentStripe,
      beltSystemKey
    ) > 0
  );
}
